import type { CartProductDetail, CartVariant } from "./cart";

/** 每个选项当前选了哪个值；没选是 undefined。 */
export type CartSelection = (string | undefined)[];

/** 数量框最多三位数：店面接口读不到库存，上限要等 Shopify 写入后才知道。 */
export const MAX_QUANTITY = 999;

/** 所有选项都选了时对应的变体。 */
export function selectedVariant(
  detail: CartProductDetail,
  selection: CartSelection,
): CartVariant | undefined {
  if (selection.some((value) => value === undefined)) return undefined;
  return detail.variants.find((variant) =>
    variant.values.every((value, index) => value === selection[index]),
  );
}

/** 还没选的选项名，按选项顺序。 */
export function missingOptions(
  detail: CartProductDetail,
  selection: CartSelection,
): string[] {
  return detail.options
    .filter((_option, index) => selection[index] === undefined)
    .map((option) => option.name);
}

/** 结合其它已选项，这个值还有没有能买的变体；没有就画成划掉的售罄样子。 */
export function valueSoldOut(
  detail: CartProductDetail,
  selection: CartSelection,
  index: number,
  value: string,
): boolean {
  return !detail.variants.some(
    (variant) =>
      variant.available &&
      variant.values[index] === value &&
      variant.values.every(
        (other, position) =>
          position === index ||
          selection[position] === undefined ||
          selection[position] === other,
      ),
  );
}

/** 推荐变体的 id 可能是数字串，也可能是 `gid://shopify/ProductVariant/123`。 */
function sameVariant(variant: CartVariant, id: string): boolean {
  const tail = id.split("/").pop();
  return tail !== undefined && tail === String(variant.id);
}

/**
 * 打开面板时的初始选择：AI 推荐的变体有货就直接选好；否则只预选只有一个取值的选项，
 * 其余留给买家选——哪怕只剩一个变体有货，也不替买家选尺码颜色。
 */
export function initialSelection(
  detail: CartProductDetail,
  recommendedId?: string,
): CartSelection {
  const recommended =
    recommendedId === undefined
      ? undefined
      : detail.variants.find((variant) => sameVariant(variant, recommendedId));
  if (recommended?.available) return [...recommended.values];
  return detail.options.map((option) =>
    option.values.length === 1 ? option.values[0] : undefined,
  );
}

/** 商品还有没有任何能买的变体。 */
export function anyAvailable(detail: CartProductDetail): boolean {
  return detail.variants.some((variant) => variant.available);
}

/** 数量框里的文字：只认正整数。 */
export function parseQuantity(text: string): number | undefined {
  if (!/^[0-9]+$/.test(text)) return undefined;
  const value = Number.parseInt(text, 10);
  return value >= 1 ? value : undefined;
}

/** 键入时只留数字、最多三位。 */
export function cleanQuantityText(text: string): string {
  return text.replace(/[^0-9]/g, "").slice(0, String(MAX_QUANTITY).length);
}
